import moment from 'moment';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Accordion } from 'react-bootstrap';
import Badge from 'react-bootstrap/Badge';
import Button from 'react-bootstrap/Button';
import Card from 'react-bootstrap/Card';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import FormControl from 'react-bootstrap/FormControl';
import Image from 'react-bootstrap/Image';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import Spinner from 'react-bootstrap/Spinner';
import Table from 'react-bootstrap/Table';
import { Link } from 'react-router-dom';
import {
  AbTestType,
  GetAbTestsConfigsQuery,
  GetUserLastSessionQuery,
  SearchUserQuery,
} from '../../../../../../__gqltypes__';

type Props = {
  account: NonNullable<SearchUserQuery['user']>;
  abTestConfigsData: GetAbTestsConfigsQuery['liveOps']['ABTestConfigs'];
  lastSession: {
    data: GetUserLastSessionQuery['analytics']['userLastSession'];
    loading: boolean;
    called: boolean;
  };
  onBanUser: () => void;
  onUnbanUser: () => void;
  onResetUserSubscriptions: () => void;
  onUpdateSubscriptionExpires: (expires: string) => Promise<unknown>;
  resetSubscriptionsLoading: boolean;
  onForceTest: (testId: string, variation: string) => void;
  forceTestLoading: boolean;
  showHistory: boolean;
};

const formatDateTimeLocalValue = (value: Date | string) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return '';
  }

  const timezoneOffset = date.getTimezoneOffset() * 60 * 1000;
  return new Date(date.getTime() - timezoneOffset).toISOString().slice(0, 16);
};


export default function AccountDisplay({
  account,
  abTestConfigsData,
  lastSession,
  onBanUser,
  onUnbanUser,
  onResetUserSubscriptions,
  onUpdateSubscriptionExpires,
  resetSubscriptionsLoading,
  onForceTest,
  forceTestLoading,
  showHistory,
}: Props) {
  const handleResetUserSubscriptions = () => {
    if (window.confirm("Are you sure you want to reset this user's subscriptions?")) {
      onResetUserSubscriptions();
    }
  };

  const { loading, called, data } = lastSession;

  const isTestEnv = process.env.REACT_APP_NAMESPACE === 'test';
  const lastOnline = (account as { lastOnline?: string | null }).lastOnline;

  const forcedTests = useMemo(() => new Set(account.ABTestForcedList.map(({ id }) => id)), [account.ABTestForcedList]);
  const abTestList = useMemo(
    () =>
      new Map([...account.ABTestList, ...account.ABTestLastAppInstallList].map(({ id, ...config }) => [id, config])),
    [account.ABTestList, account.ABTestLastAppInstallList]
  );

  const [editedTest, setEditedTest] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [editingExpires, setEditingExpires] = useState(false);
  const [newExpiresDate, setNewExpiresDate] = useState<string>('');
  const [updateExpiresLoading, setUpdateExpiresLoading] = useState(false);
  const headerContainerRef = useRef<HTMLDivElement | null>(null);
  const headerInnerRef = useRef<HTMLDivElement | null>(null);
  const [fixedCardStyle, setFixedCardStyle] = useState<React.CSSProperties>({});
  const [containerSpacerStyle, setContainerSpacerStyle] = useState<React.CSSProperties>({});
  const [isHeaderFixed, setIsHeaderFixed] = useState(false);
  const forceTest = useCallback(
    async (testId, variation) => {
      await onForceTest(testId, variation === 'unforced' ? null : variation);
      setEditedTest(null);
    },
    [onForceTest, setEditedTest]
  );
useEffect(() => {
  const navEl = document.querySelector('.navbar');
  const searchCardEl = document.querySelector('.userview-search-card') as HTMLElement | null;

  const updateHeaderPosition = () => {
    if (!headerContainerRef.current || !headerInnerRef.current || !navEl) return;

    const containerRect = headerContainerRef.current.getBoundingClientRect();
    const innerRect = headerInnerRef.current.getBoundingClientRect();
    const navRect = (navEl as HTMLElement).getBoundingClientRect();

    let topOffset = navRect.bottom;

    if (searchCardEl) {
      const searchRect = searchCardEl.getBoundingClientRect();
      const GAP = 4;
      topOffset = searchRect.bottom + GAP;
    }

    setFixedCardStyle({
      position: 'fixed',
      top: topOffset,
      left: containerRect.left,
      width: containerRect.width,
      zIndex: 20,
      backgroundColor: '#fff',
      boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
    });

    setContainerSpacerStyle({
      height: innerRect.height,
    });

    setIsHeaderFixed(true);
  };

  updateHeaderPosition();

  window.addEventListener('resize', updateHeaderPosition);

  let resizeObserver: ResizeObserver | undefined;
  if (headerContainerRef.current) {
    resizeObserver = new ResizeObserver(() => {
      updateHeaderPosition();
    });
    resizeObserver.observe(headerContainerRef.current);
  }

  return () => {
    window.removeEventListener('resize', updateHeaderPosition);
    if (resizeObserver) {
      resizeObserver.disconnect();
    }
  };
}, [account]);



  useEffect(() => {
    const el = headerInnerRef.current;
    const scrollEl = document.querySelector(
      'div#content > div.overflow-auto'
    ) as HTMLElement | null;

    if (!isHeaderFixed || !el || !scrollEl) {
      return () => {};
    }

    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      scrollEl.scrollBy({
        top: e.deltaY,
        behavior: 'auto',
      });
    };
    el.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      el.removeEventListener('wheel', handleWheel);
    };
  }, [isHeaderFixed]);

  useEffect(() => {
    if (editingExpires && account.subscriptionDetails?.expires) {
      setNewExpiresDate(formatDateTimeLocalValue(account.subscriptionDetails.expires));
    }
  }, [editingExpires, account.subscriptionDetails?.expires]);

  const handleUpdateExpires = useCallback(async () => {
    const expiresDate = new Date(newExpiresDate);
    if (!newExpiresDate || Number.isNaN(expiresDate.getTime())) {
      return;
    }

    setUpdateExpiresLoading(true);
    try {
      await onUpdateSubscriptionExpires(expiresDate.toISOString());
      setEditingExpires(false);
    } finally {
      setUpdateExpiresLoading(false);
    }
  }, [newExpiresDate, onUpdateSubscriptionExpires]);

  return (
    <>
    <div ref={headerContainerRef} style={containerSpacerStyle} className="mb-0">
        <div ref={headerInnerRef} style={fixedCardStyle}>
        <Card className="mb-0">
          <Card.Body className="py-2">
            {/* @ts-ignore */}
            <Row fluid className="align-items-center">
              <Col md="auto" className="ml-3">
                <Image
                  roundedCircle
                  className="img-profile"
                  src={account.picture ?? undefined}
                  style={{ width: 72, height: 72 }}
                />
              </Col>

              <Col>
                {/* User name + last session */}
                <Row className="mb-1">
                  <Col>
                    <div>
                      <b>User Name:</b> {account.displayName}
                    </div>
                    <div>
                      <b>User ID: </b>
                      <Link
                        className="text-decoration-none p-0 m-0 text-left"
                        to={`/sp3/devtools/table/sessions/${account.id}`}
                      >
                        {account.id}
                      </Link>
                    </div>
                  </Col>
                  <Col className="text-md-right">
                    <small>
                      <b>Last Online:</b> {lastOnline ? moment(lastOnline).format('lll') : '-'}
                    </small>
                    <br />
                    {called && !loading ? (
                      data ? (
                        <small>
                          <b>Last Session Date:</b>{' '}
                          {moment(Math.floor(data.date / 1000)).format('lll')}
                          <br />
                          <b>Last Session ID: </b>
                          <Link
                            className="text-decoration-none p-0 m-0 text-left"
                            to={`/sp3/devtools/table/session/${data.id}`}
                          >
                            {data.id}
                          </Link>
                        </small>
                      ) : (
                        <small>
                          No session found in the last 7 days
                        </small>
                      )
                    ) : (
                      <Spinner animation="border" size="sm" />
                    )}
                  </Col>
                </Row>

                {/* Subscription + email + actions */}
                <Row className="mb-1">
                  <Col md={6}>
                    <div className="mb-1">
                      <b>Subscription Status:</b> {account.subscriptionTier}
                    </div>
                        {!!account.subscriptionDetails && (
                          <small>
                            <div className="d-flex flex-wrap">
                              <div className="mr-3 mb-1">
                                <b>Active Since:</b>{" "}
                                {new Date(account.subscriptionDetails.activeSince).toLocaleString()}
                              </div>

                              <div className="mr-3 mb-1 d-flex align-items-center">
                                <div>
                                  <b>Expires:</b>{" "}
                                  {new Date(account.subscriptionDetails.expires).toLocaleString()}
                                </div>
                                {(account.subscriptionTier !== 'BASIC') && (
                                  <Button
                                    variant="link"
                                    size="sm"
                                    className="p-0 ml-2"
                                    onClick={() => setEditingExpires(!editingExpires)}
                                    title="Edit expires date">
                                    <i className="fas fa-pen" />
                                  </Button>
                                )}
                              </div>

                              <div className="mr-3 mb-1">
                                <b>Free trial:</b>{" "}
                                {account.subscriptionDetails.isFreeTrial ? "true" : "false"}
                              </div>
                              <div className="mr-3 mb-1">
                                <b>Product ID:</b> {account.subscriptionDetails.sku}
                                {"  "}
                                <span className="ml-2">
                                  <b>Auto Renews:</b>{" "}
                                  {account.subscriptionDetails.autoRenews ? "true" : "false"}
                                </span>
                              </div>
                            </div>
                          </small>
                        )}
                  </Col>
                  <Col md={6}>
                    <small>
                      <div>
                        <b>Email address: </b> {account.emailCredential}
                      </div>
                      {account.testCredential && (
                        <div>
                          <b>Test token: </b> {account.testCredential}
                        </div>
                      )}
                    </small>
                    <div className="d-flex align-items-center flex-wrap mt-2">
                      {account.role === 'BANNED' ? (
                        <>
                          <span className="mr-2 mb-1">
                            This user has been <b>BANNED</b>
                          </span>
                          <Button
                            variant="primary"
                            size="sm"
                            className="mx-2 mb-1"
                            onClick={onUnbanUser}
                          >
                            Unban User
                          </Button>
                        </>
                      ) : (
                        <Button
                          variant="danger"
                          size="sm"
                          className="mx-2 mb-1"
                          onClick={onBanUser}
                        >
                          Ban User
                        </Button>
                      )}

                      {isTestEnv &&
                        (resetSubscriptionsLoading ? (
                          <Spinner animation="border" size="sm" className="ml-2 mb-1" />
                        ) : (
                          <Button
                            variant="warning"
                            size="sm"
                            className="mx-2 mb-1"
                            onClick={handleResetUserSubscriptions}
                          >
                            Reset subscriptions
                          </Button>
                        ))}
                    </div>
                  </Col>
                </Row>
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </div>
    </div>

    <Card className="mb-2 shadow-sm border-0">
      <Card.Body className="py-2">
          <Accordion>
            <Accordion.Toggle as={Card.Title} variant="link" eventKey="0" onClick={() => setExpanded(!expanded)}>
              <div style={{ cursor: 'pointer', display: 'flex', justifyContent: 'space-between' }}>
                AB TEST
                <Button variant="link">
                  {!expanded && <i className="fas fa-chevron-down" />}
                  {expanded && <i className="fas fa-chevron-up" />}
                </Button>
              </div>
            </Accordion.Toggle>

            <Accordion.Collapse eventKey="0">
              <Table size="sm" bordered className="mb-0">
                <thead>
                  <tr>
                    <th>AB Test</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Current variation</th>
                  </tr>
                </thead>
                <tbody>
                  {abTestConfigsData.map(({ id, variationsWithPercentage, status, type }) => {
                    const forced = forcedTests.has(id);
                    const config = abTestList.get(id);
                    return (
                      <tr key={id} className="py-1 px-2 my-0">
                        <td>{id}</td>
                        <td>{type}</td>
                        <td>
                          {/* @ts-ignore */}
                          <Badge variant={{ TESTING: 'success', WINNER: 'secondary' }[status] ?? 'dark'}>
                            {/* @ts-ignore */}
                            {{ TESTING: 'Testing', WINNER: 'Winner' }[status] ?? 'Unknown'}
                          </Badge>
                        </td>
                        <td className="p-0 pl-2">
                          <div className="d-flex justify-content-between align-items-center">
                            {editedTest === id ? (
                              forceTestLoading ? (
                                <div className="flex-grow-1 text-center">
                                  <Spinner animation="border" size="sm" className="p-2 m-1" />
                                </div>
                              ) : (
                                <>
                                  <FormControl
                                    as="select"
                                    size="sm"
                                    className="flex-grow-1 mx-0"
                                    value={forced ? config?.variation : 'unforced'}
                                    onChange={(e) => forceTest(id, e.target.value)}
                                  >
                                    <option key="unforced" value="unforced">
                                      [unforced]
                                    </option>
                                    {(variationsWithPercentage ?? [config?.variation]).map((variation2) => (
                                      <option key={variation2.name} value={variation2.name}>
                                        {variation2.name}
                                      </option>
                                    ))}
                                  </FormControl>
                                  <Button
                                    size="sm"
                                    variant="danger"
                                    onClick={() => setEditedTest(null)}
                                    className="ml-2"
                                  >
                                    <i className="fas fa-times" />
                                  </Button>
                                </>
                              )
                            ) : (
                              <>
                                <span>
                                  {config !== undefined ? config.variation : 'Not started yet...'}
                                  {forced && <b> (forced)</b>}
                                </span>
                                <Button
                                  variant={forced ? 'warning' : 'primary'}
                                  size="sm"
                                  disabled={type === AbTestType.APP_INSTALL}
                                  onClick={() => setEditedTest(id)}
                                  className="ml-2"
                                >
                                  {forced ? 'Edit' : 'Force'}
                                </Button>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            </Accordion.Collapse>
          </Accordion>
        </Card.Body>
      </Card>

      <Modal
        centered
        show={editingExpires}
        onHide={() => {
          if (!updateExpiresLoading) {
            setEditingExpires(false);
          }
        }}
        backdrop={updateExpiresLoading ? 'static' : true}
        keyboard={!updateExpiresLoading}>
        <Modal.Header closeButton={!updateExpiresLoading}>
          <Modal.Title>Edit Subscription Expiry Date</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Expires</Form.Label>
            <Form.Control
              type="datetime-local"
              value={newExpiresDate}
              onChange={(e) => setNewExpiresDate(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setEditingExpires(false)} disabled={updateExpiresLoading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleUpdateExpires}
            disabled={!newExpiresDate || updateExpiresLoading}>
            {updateExpiresLoading ? <Spinner animation="border" size="sm" /> : 'Update'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
}
